import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import tokenStore from "common/tokenStore";
import { exchangeCode, startLogin } from "services/idAuth";

// Public entry at /profile/login. Three roles:
//  - Callback from the id service (has ?code=): exchange the single-use code for
//    tokens, persist them, and enter the app.
//  - Already signed in: go straight to the dashboard.
//  - Otherwise: show an explicit "Sign in" screen. Authentication is only
//    initiated on click (never automatically), so logout is observable and we
//    don't bounce back into a live id session (SSO re-login loop).
const ExternalSignIn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [exchanging, setExchanging] = useState(false);

  const loggedOut = searchParams.get("loggedout") === "1";

  useEffect(() => {
    const code = searchParams.get("code");
    if (code) {
      setExchanging(true);
      (async () => {
        try {
          const tokens = await exchangeCode(code);
          if (!tokens?.access_token) {
            throw new Error("No tokens returned from code exchange");
          }
          tokenStore.setTokens(tokens);
          navigate("/dashboard", { replace: true });
        } catch (e) {
          console.error("Auth code exchange failed", e);
          setError("Sign-in failed. Please try again.");
          setExchanging(false);
        }
      })();
      return;
    }
    // No code: if a valid session already exists, skip the login screen.
    if (tokenStore.isAuthenticated()) {
      navigate("/dashboard", { replace: true });
    }
  }, [searchParams, navigate]);

  const signIn = useCallback(() => {
    // Mints the PKCE pair, then redirects to the id service.
    startLogin();
  }, []);

  if (exchanging) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 400, mx: "auto", mt: 8, textAlign: "center" }}>
      <Typography variant="h4" gutterBottom>Admin Portal</Typography>
      {loggedOut && (
        <Typography sx={{ mb: 2 }} color="text.secondary">
          You have been signed out.
        </Typography>
      )}
      {error && (
        <Typography sx={{ mb: 2 }} color="error">{error}</Typography>
      )}
      <Button variant="contained" size="large" onClick={signIn}>
        Sign in with Didimo
      </Button>
    </Box>
  );
};

export default ExternalSignIn;
